import {ApplicationData} from '@agenium-ai/agenium-client-sdk/dist/models';
import {useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useAgenium} from '../../../../Core/agenium/ageniumContext';
import {useAgeniumUser} from '../../../../Core/hooks/useAgeniumUser';
import RootStore from '../../../../Core/mobx/RootStore';
import {calcfocusAPI} from '../../api/calcfocus';
import {
    approveInstantIulApplication,
    cancelledInstantIulApplication,
    declineInstantIulApplication,
    referInstantIulApplication,
    sendConditionalCoverageEmail,
} from '../../api/instantIul';
import {
    updateHubspotProsperDealConditionalCoverage,
    updateHubspotProsperDealDecisionApproved,
    updateHubspotProsperDealDecisionReferredToUW,
    updateHubspotProsperDealPolicyNumber,
} from '../../deal';
import {getAgeniumFieldsForDeal, getApprovalQuoteFromCalcfocus, getCalcfocusDeclineRequest} from '../utils';

class AgeniumApplicationDataMissing extends Error {
    constructor() {
        super('Agenium application data is missing. It may not have been initialized yet');
    }
}

export function useInstantApp({
    onCalcFocusApprovalQuoteChange,
}: {
    onCalcFocusApprovalQuoteChange?: (data: Awaited<ReturnType<typeof calcfocusAPI>>) => void;
} = {}) {
    const {ageniumUser} = useAgeniumUser();
    const hasDeclined = useRef(false);
    const {parameterStore, contactsFormStore} = RootStore.agentPortalStore;
    const location = useLocation();
    const history = useHistory();
    const searchParams = new URLSearchParams(location.search);

    const agenium = useAgenium();

    const referApplication = async () => {
        const calcfocusReportRequest = parameterStore.prosperRequest;

        if (!calcfocusReportRequest) {
            console.error('Missing prosperRequest on parameterStore');
            return;
        }

        const instantIulApplicationId = parameterStore.instantIulApplicationId as string;
        const policyNumber = getPolicyNumber();
        await referInstantIulApplication(parameterStore.formid, instantIulApplicationId, {
            policyNumber,
            calcfocusReportRequest,
        });
    };

    const refer = () => {
        const policyNumber = getPolicyNumber();
        if (policyNumber) updateHubspotProsperDealPolicyNumber(policyNumber);
        updateHubspotProsperDealDecisionReferredToUW().finally(async () => await contactsFormStore.saveForm());
        referApplication();
        searchParams.set('modalPage', 'DECISION_MANUAL_UNDERWRITING');
        history.push({
            search: searchParams.toString(),
        });
    };

    const getPolicyNumber = () => {
        return agenium.applicationData?.find((t) => t.key === 'Case_PolicyNumber')?.value;
    };

    const declineApplication = async () => {
        const originalCalcfocusReportRequest = parameterStore.prosperRequest;

        if (!originalCalcfocusReportRequest) {
            console.error('Missing prosperRequest on parameterStore');
            return;
        }

        const calcfocusReportRequest = getCalcfocusDeclineRequest(
            originalCalcfocusReportRequest,
            agenium.applicationData || [],
        );

        const instantIulApplicationId = parameterStore.instantIulApplicationId as string;
        const declineReason = agenium.applicationData?.find((t) =>
            t.key.includes('Case_UWDecision_Letters_Overall'),
        )?.value;
        parameterStore.prosperDeclineReason = declineReason;
        const policyNumber = getPolicyNumber();
        if (!hasDeclined.current) {
            hasDeclined.current = true;
            await declineInstantIulApplication(parameterStore.formid, instantIulApplicationId, {
                policyNumber,
                declineReason,
                calcfocusReportRequest,
            });
        }
    };

    const cancelledApplication = async () => {
        const originalCalcfocusReportRequest = parameterStore.prosperRequest;

        if (!originalCalcfocusReportRequest) {
            console.error('Missing prosperRequest on parameterStore');
            return;
        }

        const calcfocusReportRequest = getCalcfocusDeclineRequest(
            originalCalcfocusReportRequest,
            agenium.applicationData || [],
        );

        const instantIulApplicationId = parameterStore.instantIulApplicationId as string;
        const declineReason = agenium.applicationData?.find((t) =>
            t.key.includes('Case_UWDecision_Letters_Overall'),
        )?.value;
        parameterStore.prosperDeclineReason = declineReason;
        const policyNumber = getPolicyNumber();
        if (!hasDeclined.current) {
            hasDeclined.current = true;
            await cancelledInstantIulApplication(parameterStore.formid, instantIulApplicationId, {
                policyNumber,
                declineReason,
                calcfocusReportRequest,
            });
        }
    };

    const decline = async () => {
        if (!agenium.applicationData) throw new AgeniumApplicationDataMissing();

        const policyNumber = getPolicyNumber();
        if (policyNumber) updateHubspotProsperDealPolicyNumber(policyNumber);

        console.log(
            'Declined Insurance Reason: ',
            agenium.applicationData?.find((t) => t.key.includes('Case_UWDecision_Letters_Overall'))?.value,
        );
        await declineApplication();
        searchParams.set('modalPage', 'DECISION_DECLINED');
        history.push({
            search: searchParams.toString(),
        });
    };

    const cancelled = async () => {
        if (!agenium.applicationData) throw new AgeniumApplicationDataMissing();

        const policyNumber = getPolicyNumber();
        if (policyNumber) updateHubspotProsperDealPolicyNumber(policyNumber);

        console.log(
            'Withdrawn Insurance Reason: ',
            agenium.applicationData?.find((t) => t.key.includes('Case_UWDecision_Letters_Overall'))?.value,
        );
        await cancelledApplication();
        searchParams.set('modalPage', 'DECISION_DECLINED');
        history.push({
            search: searchParams.toString(),
        });
    };

    const approveApplication = async (instantIulApplicationId: string) => {
        const policyNumber = getPolicyNumber();
        await approveInstantIulApplication(parameterStore.formid, instantIulApplicationId, policyNumber);
    };

    const approve = () => {
        if (!agenium.applicationData) throw new AgeniumApplicationDataMissing();
        const policyNumber = getPolicyNumber();
        if (policyNumber) updateHubspotProsperDealPolicyNumber(policyNumber);
        getApprovalQuoteFromCalcfocus(agenium.applicationData, ageniumUser).then((response) => {
            onCalcFocusApprovalQuoteChange?.(response);
            agenium
                .updateApplicationData(
                    {
                        ageniumFields: getAgeniumFieldsForDeal(agenium.applicationData),
                        ...(response && {
                            calcfocusResponse: {
                                out: response.out,
                                faceAmount: response.faceAmount,
                                monthlyPremium: response.monthlyPremium,
                            },
                        }),
                    },
                    parameterStore.formid,
                )
                .finally(() => {
                    updateHubspotProsperDealDecisionApproved(agenium.applicationData as ApplicationData[]).finally(
                        async () => await contactsFormStore.saveForm(),
                    );
                });
        });
        searchParams.set('modalPage', 'DECISION_APPROVAL');
        history.push({
            search: searchParams.toString(),
        });
        //FIXME: this should probably be done via webhook
        approveApplication(parameterStore.instantIulApplicationId as string);
    };

    const electConditionalCoverage = async () => {
        const conditionalCoverageQuestion = agenium?.sections
            ?.find((section) => section.name === 'Final UW Decision & Rates')
            ?.questions?.find((question) => question.key === 'Case_UWDecision_ConditionalReceipt');
        if (conditionalCoverageQuestion) {
            conditionalCoverageQuestion.value = 'Yes';
            agenium.setQuestion(conditionalCoverageQuestion);
            await agenium.submitAnswer(conditionalCoverageQuestion);
            await updateHubspotProsperDealConditionalCoverage('Yes');
            await sendConditionalCoverageEmail(parameterStore.formid as string);
        }
    };

    return {
        refer,
        decline,
        approve,
        cancelled: cancelled,
        electConditionalCoverage,
    };
}
